import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { interval, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, take, takeUntil, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { NavigationService } from 'src/app/service/navigation.service';
import { DeptBookService } from 'src/app/service/deptbook.service';
import { Features } from 'controls';
import { RequestRoot } from 'types';
import { version } from 'src/version';
import { RequestFeatures } from 'src/webid/4.0/model/features';
import { RequestFeaturesV2 } from 'src/webid/4.0/model/features2';
import { RequestDataAccessService } from 'src/webid/4.0/projects/data-access/src/lib/request-data-access.service';
import { IPrintDepartment } from 'src/webid/4.0/model/print-web/model/print-department';

@Component({
    selector: 'print',
    templateUrl: './print.component.html',
    styleUrls: ['./print.component.scss'],
})
export class PrintComponent implements OnInit, OnDestroy {
    public v1: boolean = false;
    public v2: boolean = false;
    public adult: boolean = false;
    public loading: boolean = false;
    public rendered: Subject<void> = new Subject<void>();
    public request: RequestRoot | null = null;
    public department: IPrintDepartment | null = null;
    public version: string = version;

    constructor(
        private route: ActivatedRoute,
        private appService: AppService,
        private deptBookService: DeptBookService,
        private navigationService: NavigationService,
        private requestService: RequestDataAccessService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        document.body.classList.add('print');
        document.documentElement.classList.add('print');
        this.navigationService.activity.toolbar.reset();
        this.navigationService.activity.toolbar.hide();
        this.initializePrintForm().subscribe();
    }

    ngOnDestroy(): void {
        document.body.classList.remove('print');
        document.documentElement.classList.remove('print');
    }

    initializePrintForm(): Observable<void> {
        return this.getRequestId().pipe(
            tap(() => (this.loading = true)),
            concatMap((id) => this.getRequestFromDatabase(id)),
            concatMap((r) => this.getDepartmentForRequest(r)),
            concatMap((v) => this.onLoadingComplete()),
            catchError((e) => this.onLoadingFailed(e)),
            tap(() => (this.loading = false))
        );
    }

    print(): void {
        globalThis.print();
    }

    cancel(): void {
        this.navigationService.navigateBack();
    }

    private onLoadingComplete(): Observable<void> {
        this.changeDetectorRef.detectChanges();
        this.startInitializationDetector().subscribe();
        return of(void 0);
    }

    private onLoadingFailed(error: any): Observable<void> {
        console.error(error);
        return of(void 0);
    }

    private getRequestId(): Observable<string> {
        return this.route.queryParams.pipe(
            map((params) => params['id']),
            takeUntil(this.rendered),
            take(1)
        );
    }

    private getRequestFromDatabase(id: string): Observable<RequestRoot> {
        const requestRootBuffer = new RequestRoot();
        requestRootBuffer.id = id;

        return this.requestService.load(requestRootBuffer).pipe(
            tap((r) => (this.request = r)),
            tap((r) => (this.v1 = r.features instanceof RequestFeatures)),
            tap((r) => (this.v2 = r.features instanceof RequestFeaturesV2)),
            tap((r) => {
                if (r.features instanceof RequestFeatures) {
                    this.adult = r.features.isAdult();
                }
                if (r.features instanceof RequestFeaturesV2) {
                    this.adult = r.features.isAdult() || r.features.independentChild;
                }
            }),
            tap((r) => Features.updateFieldsVisibility(r.features, new r.features.constructor()))
        );
    }

    private getDepartmentForRequest(request: RequestRoot): Observable<IPrintDepartment> {
        let code = request.features.mobileOptions.department;
        return this.getDepatmentList().pipe(
            map((list) => list.filter((v) => v.code === code)[0]),
            map((d) => d as IPrintDepartment),
            tap((d) => (this.department = d))
        );
    }

    private getDepatmentList(): Observable<IPrintDepartment[]> {
        return !!this.appService.departments ? of(this.appService.departments) : this.deptBookService.list();
    }

    private startInitializationDetector(): Observable<void> {
        return interval(250).pipe(
            takeUntil(this.rendered),
            tap((n) => {
                this.updateViewState();
            }),
            map((n) => void 0)
        );
    }

    private updateViewState(): void {
        let elements = document.getElementsByClassName('page');
        if (elements.length >= 2) {
            this.rendered.next();
            this.rendered.complete();
            this.print();
        }
    }
}
